import { DEFAULT_EARLIEST, DEFAULT_LATEST } from 'constants/dateLimits';
import { ValidateJSModel } from 'forms/shared/types';
import { isPVQForm } from 'helpers/form';
import pvqLocationModelValidator from 'forms/shared/validators/models/location/pvqLocation';

import { getSafeDateTime, setToStartOfMonth } from 'helpers/date';
import {
  chargeTypes, criminalSentenceOptions, criminalOutcomeSentenceOverOneYearOptions
} from 'constants/enums/legalOptions';
import { REQUIRED } from 'constants/errors';
import { hasYesOrNo } from 'models/validate';
import { SafeDateTime } from 'helpers/types';
import { LegalCriminalChargeModel, LegalCriminalConvictionsItemModel } from '../types';

// NEW Section(29) only in PVQ
const criminalChargeItemModelValidator: ValidateJSModel<LegalCriminalChargeModel> = {
  CourtCharge: (value, attributes, attributeName, options) => ({ presence: true, hasValue: true }),
  ChargeType: (value, attributes, attributeName, options) => ({
    hasValue: {
      validator: {
        inclusion: chargeTypes,
      },
    },
  }),
  ChargeTypeOtherExplanation: (value, attributes, attributeName, options) => {
    if (attributes?.ChargeType?.value === 'Other') {
      return { hasValue: true };
    }
    return {};
  },
  CourtOutcome: {
    hasValue: {
      validator: {
        inclusion: criminalOutcomeSentenceOverOneYearOptions,
      },
    },
  },
  CourtOutcomeOtherExplanation: (value, attributes, attributeName, options) => {
    if (attributes?.CourtOutcome?.value === 'Other') {
      return { presence: true, hasValue: true };
    }
    return {};
  },

  ConvictedDate: (value, attributes, attributeName, options) => {
    const offenceDate: SafeDateTime = getSafeDateTime(options?.parentAttributes?.OffenseDate);

    if (isPVQForm(options?.formType)) {
      if (attributes?.CourtOutcome?.value === 'Guilty') {
        if (!offenceDate.valid) {
          return {
            presence: true,
            requireTrue: {
              message: 'error.enterOffenseDate',
            }
          };
        }

        return {
          presence: true,
          date: {
            earliest: offenceDate.datetime,
            latest: DEFAULT_LATEST,
            tooEarly: 'error.beforeOffense',

          },
        };
      }
    }

    return {};
  },
  SentenceJudgements: (value, attributes, attributeName, options) => {
    if (attributes?.CourtOutcome?.value === 'Guilty') {
      return {
        presence: true,
        array: {
          validator: {
            presence: true,
            inclusion: criminalSentenceOptions,
          },
          length: { minimum: 1, message: REQUIRED },
        },
      };
    }
    return {};
  },

  SentenceJudgementOtherExplanation: (value, attributes, attributeName, options) => {
    if (attributes?.SentenceJudgements?.values?.includes('Other')) {
      return { presence: true, hasValue: true };
    }
    return {};
  },

  ProbationDates: (value, attributes, attributeName, options) => {
    const convictionDate: SafeDateTime = getSafeDateTime(attributes?.ConvictedDate);
    if (isPVQForm(options?.formType)) {
      const validationCondition = !!value?.year;
      if (validationCondition || attributes?.SentenceJudgements?.values?.includes('Probation')) {
        if (!convictionDate.valid) {
          return {
            presence: true,
            requireTrue: {
              message: 'error.enterConvictionDate',
            }
          };
        }
        return {
          presence: true,
          daterange: {
            earliest: (convictionDate.datetime),
            tooEarly: 'error.beforeConvicted',
            latest: DEFAULT_LATEST
          },
        };
      }
    }
    return {};
  },
  IncarcerationDates: (value, attributes, attributeName, options) => {
    const convictionDate: SafeDateTime = getSafeDateTime(attributes?.ConvictedDate);
    if (attributes?.SentenceJudgements?.values?.includes('Imprisonment')) {
      if (!convictionDate.valid) {
        return {
          presence: true,
          requireTrue: {
            message: 'error.enterConvictionDate',
          }
        };
      }
      return {
        presence: true,
        daterange: {
          earliest: (convictionDate.datetime),
          tooEarly: 'error.beforeConvicted',
          latest: DEFAULT_LATEST
        },
      };
    }
    return {};
  },

  GrantedParole: (value, attributes, attributeName, options) => {
    if (attributes?.CourtOutcome?.value === 'Guilty') {
      return { presence: true, hasValue: { validator: hasYesOrNo } };
    }
    return {};
  },

  GrantedParoleDates: (value, attributes, attributeName, options) => {
    const convictionDate: SafeDateTime = getSafeDateTime(attributes?.ConvictedDate);
    if (isPVQForm(options?.formType)) {
      const validationCondition = attributes?.GrantedParole?.value === 'Yes';
      if (validationCondition) {
        if (!convictionDate.valid) {
          return {
            presence: true,
            requireTrue: {
              message: 'error.enterConvictionDate',
            }
          };
        }

        return {
          presence: true,
          daterange: {
            earliest: (convictionDate.datetime),
            tooEarly: 'error.beforeConvicted',
            latest: DEFAULT_LATEST
          },
        };
      }
    }
    return {};
  },

};

const legalCriminalConvictionsItemValidator: ValidateJSModel<LegalCriminalConvictionsItemModel> = {
  IsReportedInAnotherSec: (value, attributes, attributeName, options) => ({ presence: true, hasValue: true }),
  NatureOfAction: (value, attributes, attributeName, options) => {
    if (attributes?.IsReportedInAnotherSec?.value === 'No') {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  OffenseDate: (value, attributes, attributeName, options = {}) => {
    const applicantBirthdate = getSafeDateTime(setToStartOfMonth(options?.applicantBirthdate));
    if (attributes?.IsReportedInAnotherSec?.value === 'No') {
      return {
        presence: true,
        date: {
          earliest: (applicantBirthdate?.datetime || DEFAULT_EARLIEST),
          latest: DEFAULT_LATEST,
          tooEarly: 'error.applicantMin',
          requireDay: false,
        },
      };
    }
    return {};
  },
  InvolvedViolence: (value, attributes, attributeName, options) => {
    if (attributes?.IsReportedInAnotherSec?.value === 'No') {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  InvolvedFirearms: (value, attributes, attributeName, options) => {
    if (attributes?.IsReportedInAnotherSec?.value === 'No') {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  InvolvedExplosives: (value, attributes, attributeName, options) => {
    if (attributes?.IsReportedInAnotherSec?.value === 'No') {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  InvolvedDrugs: (value, attributes, attributeName, options) => {
    if (attributes?.IsReportedInAnotherSec?.value === 'No') {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  InvolvedAlcohol: (value, attributes, attributeName, options) => {
    if (attributes?.IsReportedInAnotherSec?.value === 'No') {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  Charges: (value, attributes, attributeName, options) => {
    if (attributes?.IsReportedInAnotherSec?.value === 'No') {
      return { presence: true, accordion: { validator: criminalChargeItemModelValidator } };
    }
    return {};
  },
  CaseClosedDate: (value, attributes, attributeName, options = {}) => {
    const offenceDate: SafeDateTime = getSafeDateTime(attributes?.OffenseDate);
    if (attributes?.IsReportedInAnotherSec?.value === 'No') {
      if (attributes?.CaseNotClosed?.checked === true) {
        return {};
      }

      if (!offenceDate.valid) {
        return {
          presence: true,
          requireTrue: {
            message: 'error.enterOffenseDate',
          }
        };
      }
      return {
        presence: true,
        date: {
          earliest: offenceDate.datetime,
          latest: DEFAULT_LATEST,
          tooEarly: 'error.beforeOffense',
          requireYear: false,
        },
      };
    }
    return {};
  },
  CaseNotClosed: (value, attributes, attributeName, options) => {
    if (attributes?.IsReportedInAnotherSec?.value === 'No') {
      return {};
    }
    return {};
  },
  CaseNotClosedExplanation: (value, attributes, attributeName, options) => {
    if (attributes?.IsReportedInAnotherSec?.value === 'No') {
      if (attributes?.CaseNotClosed?.checked === true) {
        return { presence: true, hasValue: true };
      }
      return {};
    }
    return {};
  },
  CourtName: (value, attributes, attributeName, options) => {
    if (attributes?.IsReportedInAnotherSec?.value === 'No') {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  CourtAddress: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.IsReportedInAnotherSec?.value === 'No') {
      return {
        location: {
          requireStreet: value?.inUSA,
          requireState: value?.inUSA,
          validator: pvqLocationModelValidator
        }
      };
    }
    return {};
  },

};

export default legalCriminalConvictionsItemValidator;
